import React, { useState, useEffect } from 'react';
import Header from '@/components/ui/Header';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import CareerTransitionMatrix from './components/CareerTransitionMatrix';
import IndustryExploration from './components/IndustryExploration';
import TransitionTimeline from './components/TransitionTimeline';
import RiskAssessment from './components/RiskAssessment';
import MentorMatching from './components/MentorMatching';
import ProgressTracking from './components/ProgressTracking';
import apiRequest from '@/services/requests';

const CareerPivot = () => {
const [currentStep, setCurrentStep] = useState('analysis');
const [loading, setLoading] = useState(false);
const [currentProfile, setCurrentProfile] = useState(null);
const [careerPivots, setCareerPivots] = useState([]);
const [selectedTargetRole, setSelectedTargetRole] = useState(null);
const [selectedPivotId, setSelectedPivotId] = useState(null);
const [transitionPlan, setTransitionPlan] = useState(null);
const [showRiskAssessment, setShowRiskAssessment] = useState(false);
const [showMentorMatching, setShowMentorMatching] = useState(false);
const [mentors, setMentors] = useState([]);
const [riskData, setRiskData] = useState(null);

const steps = [
{ id: 'analysis', title: 'Current Analysis', icon: 'User', completed: true },
{ id: 'exploration', title: 'Role Exploration', icon: 'Search', completed: currentStep !== 'analysis' },
{ id: 'planning', title: 'Transition Planning', icon: 'Map', completed: false },
{ id: 'execution', title: 'Execute Plan', icon: 'Play', completed: false }
];

// Fetch current profile on mount
useEffect(() => {
fetchCurrentProfile();
}, []);

// Fetch current profile
const fetchCurrentProfile = async () => {
setLoading(true);
try {
const response = await apiRequest.getCurrentProfile();
if (response.success) {
setCurrentProfile(response.profile);
}
} catch (error) {
console.error('Error fetching profile:', error);
// Handle error - show notification
} finally {
setLoading(false);
}
};

// Analyze career pivots
const analyzeCareerOptions = async () => {
setLoading(true);
try {
const response = await apiRequest.analyzeCareerPivots();
if (response.success) {
setCurrentProfile(response.currentProfile);
setCareerPivots(response.careerPivots);
setCurrentStep('exploration');
}
} catch (error) {
console.error('Error analyzing career pivots:', error);
// Handle error - show notification
} finally {
setLoading(false);
}
};

// Calculate compatibility score
const calculateCompatibility = (targetRole) => {
if (!targetRole || !currentProfile) return 0;
return targetRole.compatibilityScore || 0;
};

// Handle role selection
const handleRoleSelection = async (role) => {
setLoading(true);
try {
// Save the career pivot
const pivotData = {
currentRole: currentProfile.currentRole,
currentIndustry: currentProfile.industry,
currentSkills: currentProfile.skills,
targetRole: role.title,
targetIndustry: role.industry,
requiredSkills: role.requiredSkills,
skillGap: role.requiredSkills.filter(skill => 
!currentProfile.skills.some(s => 
s.toLowerCase().includes(skill.toLowerCase())
)
),
compatibilityScore: role.compatibilityScore,
transitionDifficulty: role.transitionDifficulty,
estimatedDuration: role.estimatedTimeToTransition,
salaryProjection: role.salaryRange
};

const response = await apiRequest.saveCareerPivot(pivotData);

if (response.success) {
setSelectedTargetRole(role);
setSelectedPivotId(response.careerPivot.id);
setCurrentStep('planning');
}
} catch (error) {
console.error('Error saving career pivot:', error);
// Handle error - show notification
} finally {
setLoading(false);
}
};

// Generate transition plan
const generateTransitionPlan = async () => {
if (!selectedPivotId) return;

setLoading(true);
try {
const response = await apiRequest.generateTransitionPlan(selectedPivotId);

if (response.success) {
setTransitionPlan(response.transitionPlan);
}
} catch (error) {
console.error('Error generating transition plan:', error);
// Handle error - show notification
} finally {
setLoading(false);
}
};

// Load risk assessment
const loadRiskAssessment = async () => {
if (!selectedPivotId) return;

setLoading(true);
try {
const response = await apiRequest.assessRisks(selectedPivotId);

if (response.success) {
setRiskData(response.riskAssessment);
setShowRiskAssessment(true);
}
} catch (error) {
console.error('Error assessing risks:', error);
// Handle error - show notification
} finally {
setLoading(false);
}
};

// Load mentors
const loadMentors = async () => {
if (!selectedPivotId) return;

setLoading(true);
try {
const response = await apiRequest.findMentors(selectedPivotId);

if (response.success) {
setMentors(response.mentors);
setShowMentorMatching(true);
}
} catch (error) {
console.error('Error finding mentors:', error);
// Handle error - show notification
} finally {
setLoading(false);
}
};

// Update transition plan
const handleUpdatePlan = async (updatedPlan) => {
if (!transitionPlan?.id) return;

try {
const response = await apiRequest.updateTransitionPlan(
transitionPlan.id,
updatedPlan
);

if (response.success) {
setTransitionPlan(response.transitionPlan);
}
} catch (error) {
console.error('Error updating plan:', error);
// Handle error - show notification
}
};

if (loading && !currentProfile) {
return (
<div className="min-h-screen bg-background flex items-center justify-center">
<div className="text-center">
<Icon name="Loader" className="animate-spin text-primary mx-auto mb-4" size={48} />
<p className="text-text-secondary">Loading your profile...</p>
</div>
</div>
);
}

return (
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">
{/* Page Header */}
<div className="mb-8">
<div className="flex items-center justify-between mb-6">
<div className="flex items-center space-x-3">
<div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
<Icon name="Route" size={24} className="text-primary" />
</div>
<div>
<h1 className="text-3xl font-bold text-text-primary">Career Pivot</h1>
<p className="text-text-secondary">Strategic career transition planning with AI insights</p>
</div>
</div>
<div className="flex items-center space-x-3">
<Button
variant="outline"
onClick={loadRiskAssessment}
iconName="AlertTriangle"
disabled={!selectedPivotId || loading}
>
Risk Assessment
</Button>
<Button
variant="outline"
onClick={loadMentors}
iconName="Users"
disabled={!selectedPivotId || loading}
>
Find Mentor
</Button>
</div>
</div>

{/* Progress Steps */}
<div className="flex items-center space-x-8 mb-6">
{steps.map((step, index) => (
<div key={step.id} className="flex items-center">
<div className="flex items-center space-x-3">
<div className={`w-10 h-10 rounded-full flex items-center justify-center ${
currentStep === step.id
? 'bg-primary text-white'
: step.completed
? 'bg-success text-white'
: 'bg-muted text-text-secondary'
}`}>
{step.completed && currentStep !== step.id ? (
<Icon name="Check" size={16} />
) : (
<Icon name={step.icon} size={16} />
)}
</div>
<div>
<p className={`text-sm font-medium ${
currentStep === step.id ? 'text-primary' : 'text-text-secondary'
}`}>
{step.title}
</p>
</div>
</div>
{index < steps.length - 1 && (
<div className={`w-8 h-0.5 mx-4 ${
step.completed ? 'bg-success' : 'bg-border'
}`} />
)}
</div>
))}
</div>
</div>

{/* Main Content */}
<div className="space-y-8">
{/* Current Situation Analysis */}
{currentStep === 'analysis' && currentProfile && (
<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
<div className="bg-card border border-border rounded-lg p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Current Profile</h3>
<div className="space-y-4">
<div>
<p className="text-sm font-medium text-text-secondary">Current Role</p>
<p className="text-lg text-text-primary">{currentProfile.currentRole}</p>
</div>
<div>
<p className="text-sm font-medium text-text-secondary">Experience</p>
<p className="text-lg text-text-primary">{currentProfile.yearsExperience} years</p>
</div>
<div>
<p className="text-sm font-medium text-text-secondary">Industry</p>
<p className="text-lg text-text-primary">{currentProfile.industry}</p>
</div>
{currentProfile.salary && (
<div>
<p className="text-sm font-medium text-text-secondary">Current Salary</p>
<p className="text-lg text-text-primary">${currentProfile.salary.toLocaleString()}</p>
</div>
)}
<div>
<p className="text-sm font-medium text-text-secondary mb-2">Skills</p>
<div className="flex flex-wrap gap-2">
{currentProfile.skills?.map(skill => (
<span key={skill} className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
{skill}
</span>
))}
</div>
</div>
</div>
</div>

<div className="space-y-6">
{currentProfile.strengths && currentProfile.strengths.length > 0 && (
<div className="bg-card border border-border rounded-lg p-6">
<h3 className="text-lg font-semibold text-text-primary mb-4">Career Strengths</h3>
<div className="space-y-3">
{currentProfile.strengths.map(strength => (
<div key={strength} className="flex items-center space-x-3">
<Icon name="CheckCircle" size={16} className="text-success" />
<span className="text-text-primary">{strength}</span>
</div>
))}
</div>
</div>
)}

<Button
onClick={analyzeCareerOptions}
className="w-full"
iconName="ArrowRight"
disabled={loading}
>
{loading ? 'Analyzing...' : 'Explore Career Options'}
</Button>
</div>
</div>
)}

{/* Role Exploration */}
{currentStep === 'exploration' && (
<div className="space-y-6">
<IndustryExploration />

<div>
<h3 className="text-lg font-semibold text-text-primary mb-4">
{loading ? 'AI is analyzing your career options...' : 'Potential Career Paths'}
</h3>

{loading ? (
<div className="text-center py-12">
<Icon name="Loader" className="animate-spin text-primary mx-auto mb-4" size={48} />
<p className="text-text-secondary">This may take a moment...</p>
</div>
) : careerPivots.length > 0 ? (
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{careerPivots.map((role, index) => (
<div key={index} className="bg-card border border-border rounded-lg p-6 hover:shadow-elevation-2 spring-transition">
<div className="flex items-start justify-between mb-4">
<div>
<h4 className="text-lg font-semibold text-text-primary mb-1">{role.title}</h4>
<p className="text-sm text-text-secondary">{role.industry}</p>
</div>
<div className={`px-2 py-1 rounded-full text-xs font-medium ${
role.transitionDifficulty === 'easy'
? 'bg-success/10 text-success'
: role.transitionDifficulty === 'medium'
? 'bg-warning/10 text-warning'
: 'bg-destructive/10 text-destructive'
}`}>
{role.transitionDifficulty}
</div>
</div>

<div className="space-y-3 mb-4">
{role.salaryRange && (
<div className="flex items-center justify-between text-sm">
<span className="text-text-secondary">Salary Range:</span>
<span className="font-medium text-text-primary">
${role.salaryRange.min?.toLocaleString()} - ${role.salaryRange.max?.toLocaleString()}
</span>
</div>
)}
<div className="flex items-center justify-between text-sm">
<span className="text-text-secondary">Growth:</span>
<span className={`font-medium ${
role.growthProjection === 'high' ? 'text-success' : 'text-warning'
}`}>
{role.growthProjection}
</span>
</div>
<div className="flex items-center justify-between text-sm">
<span className="text-text-secondary">Demand:</span>
<span className="font-medium text-text-primary">{role.demandLevel}</span>
</div>
<div>
<div className="flex items-center justify-between text-sm mb-1">
<span className="text-text-secondary">Skill Match:</span>
<span className="font-medium text-text-primary">
{calculateCompatibility(role)}%
</span>
</div>
<div className="w-full bg-muted rounded-full h-2">
<div
className="bg-primary h-2 rounded-full"
style={{ width: `${calculateCompatibility(role)}%` }}
/>
</div>
</div>
</div>

<Button
onClick={() => handleRoleSelection(role)}
className="w-full"
variant="outline"
disabled={loading}
>
Create Transition Plan
</Button>
</div>
))}
</div>
) : (
<div className="text-center py-12 bg-card border border-border rounded-lg">
<p className="text-text-secondary">No career options available yet.</p>
</div>
)}
</div>
</div>
)}

{/* Transition Planning */}
{currentStep === 'planning' && selectedTargetRole && (
<div className="space-y-6">
<CareerTransitionMatrix
currentProfile={currentProfile}
targetRole={selectedTargetRole}
/>

{!transitionPlan ? (
<div className="text-center py-8">
<Button
onClick={generateTransitionPlan}
size="lg"
iconName="Zap"
disabled={loading}
>
{loading ? 'Generating Plan...' : 'Generate Transition Plan'}
</Button>
</div>
) : (
<TransitionTimeline
plan={transitionPlan}
onUpdatePlan={handleUpdatePlan}
/>
)}
</div>
)}

{/* Progress Tracking */}
{transitionPlan && (
<ProgressTracking
plan={transitionPlan}
currentProfile={currentProfile}
targetRole={selectedTargetRole}
/>
)}
</div>
</div>
</main>

{/* Risk Assessment Modal */}
{showRiskAssessment && riskData && (
<RiskAssessment
currentProfile={currentProfile}
targetRole={selectedTargetRole}
riskData={riskData}
onClose={() => setShowRiskAssessment(false)}
/>
)}

{/* Mentor Matching Modal */}
{showMentorMatching && (
<MentorMatching
mentors={mentors}
targetRole={selectedTargetRole}
transitionPlanId={transitionPlan?.id}
onClose={() => setShowMentorMatching(false)}
/>
)}
</div>
);
};

export default CareerPivot;
